import {
  useState,
  useRef,
  useMemo,
  useDeferredValue,
  useCallback,
  useEffect,
} from "react";
import type { ReactNode } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  FloatingList,
  FloatingFocusManager,
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
  useId,
} from "@floating-ui/react";
import { fuzzy_search_airport } from "@jetblue/fuzzy-search";
import { InputText } from "@jetblue/kit/input-text";
import { IcChevronLeft } from "@jetblue/kit-icons/ic-chevron-left";
import { IconButton } from "@jetblue/kit/icon-button";
import type { StatelessFlightSearchFormProperties } from "../../stateless";
import { TextHighlight } from "./text-highlight";
type InputCityLabels = {
  close: string;
  input: string;
};

export type InputCityProperties = {
  className?: string | undefined;
  selectedKey: string | undefined;
  setSelectedKey: (selectedKey: string) => void;
  labels: InputCityLabels;
  items?:
    | NonNullable<StatelessFlightSearchFormProperties["odQuery"]["data"]>
    | undefined;
  isRequired?: boolean | undefined;
  name?: string | undefined;
  error?: ReactNode | undefined;
  showValidation?: boolean | undefined;
};
export const InputCity = ({
  labels,
  className = "",
  items = [],
  selectedKey = "",
  setSelectedKey,
  isRequired,
  error,
  showValidation,
  name,
  ...properties
}: InputCityProperties) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const listboxId = useId();
  const isIOS = /iPhone|iPod/.test(navigator.userAgent);
  const flatMap = useMemo(
    () => items.flatMap((airport) => [airport, ...(airport.cc ?? [])]),
    [items],
  );
  const getInputText = () => {
    const airport =
      flatMap.find(({ code }) => code === selectedKey)?.name ?? "";
    setInputValue(airport);
  };
  const { refs, context, floatingStyles } = useFloating({
    middleware: [
      offset(1),
      flip(),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            height: `${availableHeight.toString()}px`,
            minWidth: "365px",
            width: `${rects.reference.width.toString()}px`,
          });
        },
      }),
    ],
    onOpenChange: (nextOpen) => {
      setIsOpen(nextOpen);
      if (!nextOpen) {
        getInputText();
      }
    },
    open: isOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const listReference = useRef<(HTMLElement | null)[]>([]);
  const parentReference = useRef<HTMLDivElement>(null);
  const scrollElement = useRef<HTMLUListElement>(null);
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [
      useDismiss(context, {
        outsidePress: (event) =>
          !parentReference.current?.contains(event.target as Node | null),
      }),
      useListNavigation(context, {
        activeIndex,
        listRef: listReference,
        onNavigate: setActiveIndex,
        selectedIndex,
        virtual: true,
      }),
      useFocus(context, {
        enabled: !isOpen,
      }),
    ],
  );
  const deferredInputValue = useDeferredValue(inputValue);
  const filteredItems = useMemo(() => {
    const target = deferredInputValue.toLowerCase();
    return fuzzy_search_airport(target, items, 0.75).flatMap(
      ({ candidate: airport }) =>
        [
          airport,
          ...(airport.cc ?? []).map((a) => ({ ...a, indent: true })),
        ] as (typeof airport & { indent?: boolean })[],
    );
  }, [items, deferredInputValue]);
  useEffect(() => {
    setSelectedIndex(
      filteredItems.findIndex(({ code }) => code === selectedKey),
    );
  }, [selectedKey, filteredItems]);
  useEffect(() => {
    getInputText();
  }, [selectedKey]);
  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    enabled: isOpen,
    estimateSize: () => 40,
    getScrollElement: () => scrollElement.current,
    overscan: 5,
  });
  const handleSelect = useCallback(
    (index: number | null) => {
      if (index !== null) {
        const { code, name } = filteredItems[index] ?? {};
        if (code && name) {
          setInputValue(name);
          setSelectedKey(code);
        }
      }
      setIsOpen(false);
    },
    [filteredItems, setInputValue, setSelectedKey, setIsOpen],
  );
  return (
    <div
      className={`group/input-city flex flex-col data-[is-open='true']:relative max-md:data-[is-open='true']:fixed  max-md:data-[is-open='true']:inset-0 max-md:data-[is-open='true']:z-[999] max-md:data-[is-open='true']:bg-white ${className}`}
      {...properties}
      data-is-open={isOpen}
      ref={parentReference}
      tabIndex={-1}
    >
      <div
        className={`flex items-center 
            max-md:group-data-[is-open='true']/input-city:flex max-md:group-data-[is-open='true']/input-city:gap-2 
            max-md:group-data-[is-open='true']/input-city:p-4 max-md:group-data-[is-open='true']/input-city:pl-2 
            max-md:group-data-[is-open='true']/input-city:shadow-jb-1`}
      >
        <IconButton
          aria-label={labels.close}
          className="hidden min-w-12 border-none max-md:group-data-[is-open='true']/input-city:block"
          onPress={() => {
            setIsOpen(false);
          }}
          size="medium"
          variant="standard"
        >
          <IcChevronLeft className="size-4" />
        </IconButton>
        <div className="flex-1">
          <InputText
            autoComplete="off"
            error={error}
            inputProperties={{
              ref: refs.setReference,
              ...getReferenceProps({
                onKeyDown: (event) => {
                  if (
                    event.key === "Enter" ||
                    (event.key === " " && activeIndex !== null)
                  ) {
                    event.preventDefault();
                    handleSelect(activeIndex);
                  }
                  if (event.key === "Tab") {
                    setIsOpen(false);
                  }
                },
                ...(isIOS
                  ? {
                      onTouchEnd: (event) => {
                        (event.target as HTMLInputElement).focus();
                      },
                      onTouchStart: () => {
                        setIsOpen(true);
                      },
                    }
                  : { undefined }),
              }),
              "aria-activedescendant":
                activeIndex !== null &&
                activeIndex >= 0 &&
                filteredItems[activeIndex]
                  ? `airport-option-${filteredItems[activeIndex].code}`
                  : undefined,
              "aria-autocomplete": "list",
              "aria-controls": listboxId,
              "aria-expanded": isOpen,
              "aria-haspopup": "listbox",
              role: "combobox",
            }}
            isInvalid={showValidation ? !selectedKey : undefined}
            isRequired={isRequired}
            label={labels.input}
            onChange={(event) => {
              virtualizer.scrollToIndex(0);
              setInputValue(event.target.value);
            }}
            selectOnFocus
            value={inputValue}
          />
          <input className="hidden" name={name} readOnly value={selectedKey} />
        </div>
      </div>
      {isOpen && (
        <FloatingFocusManager context={context} initialFocus={-1} modal={false}>
          <FloatingList elementsRef={listReference}>
            <div
              ref={refs.setFloating}
              {...getFloatingProps()}
              className="z-20 bg-white max-md:contents md:max-h-80 md:overflow-auto md:rounded-b-md md:border md:border-medium-gray"
              style={{ ...floatingStyles }}
              tabIndex={-1}
            >
              <ul
                aria-label={labels.input}
                className="relative max-h-full overflow-scroll bg-white md:max-h-80"
                id={listboxId}
                ref={scrollElement}
                style={{
                  height: `${virtualizer.getTotalSize().toString()}px`,
                }}
              >
                {virtualizer.getVirtualItems().map((item) => {
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- safety: they are based on the same list
                  const { indent, code, name } = filteredItems[item.index]!;
                  return (
                    <li
                      aria-posinset={item.index + 1}
                      aria-selected={activeIndex === item.index}
                      aria-setsize={filteredItems.length}
                      className="absolute flex w-full cursor-pointer px-4 py-2 text-royal-blue hover:bg-royal-blue hover:text-white aria-selected:bg-royal-blue aria-selected:text-white"
                      data-indent={indent}
                      id={`airport-option-${code}`}
                      key={code}
                      ref={(node) => {
                        listReference.current[item.index] = node;
                      }}
                      role="option"
                      style={{
                        transform: `translateY(${item.start.toString()}px)`,
                      }}
                      tabIndex={-1}
                      {...getItemProps({
                        onClick: () => {
                          handleSelect(item.index);
                        },
                      })}
                    >
                      {indent && (
                        <span
                          aria-hidden
                          className="mr-0.5 before:content-['\2014']"
                        />
                      )}
                      <TextHighlight highlight={inputValue}>
                        {name}
                      </TextHighlight>
                    </li>
                  );
                })}
              </ul>
            </div>
          </FloatingList>
        </FloatingFocusManager>
      )}
    </div>
  );
};
